import { useReducer } from 'react';
import { FormField, TextInput, Select } from 'grommet';
import { Details, File, FormMarkup } from '@components/basicComponents';
import { getSortedFormFields, handleFormSubmit } from '@utils/forms';
import type { FormExtendedEvent } from 'grommet/components/Form';
import type { FormProps } from '@customTypes/types';
import type { LoginSupportGLCPFormState } from '@customTypes/stateTypes';
import { helpWithOptions } from './LoginSupportGLCPFormOptions';

const formTitle: string = 'HPE GreenLake Sign in Support';

export const LoginSupportGLCPForm = (props: FormProps): JSX.Element => {
  const [state, updateState] = useReducer(
    (prev: LoginSupportGLCPFormState, newState: Partial<LoginSupportGLCPFormState>) => ({
      ...prev,
      ...newState,
    }),
    {
      helpWith: '',
      username: false,
      showDetails: false,
      numFiles: 0,
    }
  );

  const onFileChange = (value: number): void => {
    updateState({ numFiles: value });
  };

  const onSubmit = ({ target }: FormExtendedEvent): void => {
    const namespace = 'hpe_loginsupport_glcp';
    const subject = `Login issue (GPST): ${state.helpWith} - {formId}`;
    const data = getSortedFormFields(target, namespace, subject);

    handleFormSubmit(data, props.onSuccess, props.onError, props.onLoading);
  };

  const onReset = (): void => {
    clearFirstSelectState();
    updateState({ helpWith: '', numFiles: 0 });
  };

  const helpWithChange = (option: string): void => {
    if (option === state.helpWith) {
      return;
    }

    clearFirstSelectState();
    updateState({ helpWith: option, username: option !== 'Other', showDetails: true });
  };

  const clearFirstSelectState = (): void => {
    updateState({ username: false, showDetails: false });
  };

  return (
    <FormMarkup
      onSubmit={onSubmit}
      onReset={onReset}
      formTitle={formTitle}
      disclaimer={<>Please enter the requested information in <b>English</b> only. Currently, Sign In Support requests cannot be processed in other languages.</>}
      formFields={
        <>
          <FormField name="helpWith" htmlFor="help-with" label="What do you need help with?" required>
            <Select
              id="help-with"
              name="helpWith"
              placeholder="Select"
              options={Object.values(helpWithOptions)}
              onChange={({ option }) => helpWithChange(option)}
            />
          </FormField>

          {state.username &&
            <FormField name="username" htmlFor="username" label="Username" required>
              <TextInput id="username" name="username" placeholder="Username" />
            </FormField>
          }

          {state.showDetails &&
            <>
              <Details />

              <FormField name="application" htmlFor="application-url" label="Application URL" required>
                <TextInput id="application-url" name="application" placeholder="Application URL (Ex : https://common.cloud.hpe.com/)" />
              </FormField>

              <File numFiles={state.numFiles} onFileChange={onFileChange} />
            </>
          }
        </>
      }
    />
  );
};
